import {
  Component,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import { HttpHeaders } from "@angular/common/http";
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { map, Observable, Subject, tap, firstValueFrom } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { NodeDetailsPopupComponent } from "src/app/common-components/node-details-popup/node-details-popup.component";
import { CustomObjectTypeEnum } from "src/app/enums/customObjectType.enum";
import { OperatorTypeEnum } from "src/app/enums/operatorType.enum";
import {
    EndDeviceAssignmentDialogComponent,
    IEndDeviceAssignmentDialogData,
} from "src/app/modals/end-device-assignment-dialog/end-device-assignment-dialog.component";
import {
    IMultipleNodeAssignmentFormDialogData,
    MultipleSensorAssignmentDialog,
} from "src/app/modals/node-assignment-dialog/multiple-sensor-assignment-dialog.component";
import {
    INodeAssignmentFormDialogData,
    NodeAssignmentDialogComponent,
} from "src/app/modals/node-assignment-dialog/node-assignment-dialog.component";
import {
    CustomEntityDialogData,
    NodeOperationsComponent,
} from "src/app/modals/node-operations/node-operations.component";
import { CustomEntity } from "src/app/models/customEntity.model";
import { CustomObjectLookup } from "src/app/models/customObjectLookup.model";
import { UserIdentity } from "src/app/models/userIdentity.model";
import { AuthService } from "src/app/services";
import { BatteryService } from "src/app/services/battery.service";
import { CommonService } from "src/app/services/common.service";
import { CustomEntityService } from "src/app/services/customEntity.service";
import { CustomObjectService } from "src/app/services/customObject.service";
import { GridViewService } from "src/app/services/grid-view.service";
import { ToastService } from "src/app/services/toast.service";
import { TranslateService } from "src/app/services/translate.service";
import { SegService } from "src/app/services/seg.service";
import { OrganizationLevelService } from "src/app/services/organizationLevel.service";
import { MappingGroupService } from "src/app/services/mapping-group.service";
import { CustomMapBuildingService } from "src/app/services/custom-map-building.service";
import { DeleteDialog } from "src/app/shared/delete-dialog/delete-dialog.component";
import { FlyoutContainerComponent } from "src/app/shared/flyout/flyout-container/flyout-container.component";
import nameOf from "src/app/shared/form/nameOf.helper";
import {
    ChildColumnConfig,
    ColumnConfig,
} from "src/app/shared/grid/core-grid/columnConfig.model";
import { GridView } from "src/app/models/gridView.model";
import { CoreGridComponent } from "src/app/shared/grid/core-grid/core-grid.component";
import { OverviewConfig } from "src/app/shared/grid/core-grid/overviewConfig.model";
import { GridSettingsService } from "src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service";
import { ISearchFieldData } from "src/app/shared/grid/baseGrid.component";
import { workforceRouteConfig } from "src/app/shared/constants/workforceRouteConfig.constants";
import { RolePermissionObjects } from "src/app/shared/constants/rolePermissionObjects.constants";
import { RoutePaths } from "src/app/shared/constants/routePaths.constants";
import {
    GridPermissions,
    gridToPermissionFlags,
} from "src/app/shared/grid/core-grid/gridPermissions.enum";

@Component({
  standalone: false,
    selector: "app-endpoints-table",
    templateUrl: "./../../shared/grid/core-grid/core-grid.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        "./../../shared/grid/core-grid/core-grid.component.scss",
        "./endpoints-table.component.scss",
    ],
})
export class EndpointsTableComponent
    extends CoreGridComponent<CustomEntity>
    implements OnInit, OnDestroy
{
    private destroy$ = new Subject<void>();
    readonly DEFAULT_END_POINT_ICON: string = "fas fa-desktop";
    @ViewChild(FlyoutContainerComponent) flyout!: FlyoutContainerComponent;

    currentUser: UserIdentity;
    nodes: CustomObjectLookup[] = [];
    endPointName: string;
    endPointType: CustomObjectTypeEnum;
    endPointIcon: string;
    lowBatteryCount: number;
    onlineCount: number;
    offlineCount: number;
    assignedCount: number;
    unassignedCount: number;

    pendingGridView: GridView | null = null;
    dynamicColumnsLoaded = false;
    gridLifecycleInitialized = false;
    staticDefaultColumnKeys: string[] = [];

    constructor(
        private route: ActivatedRoute,
        private customObjectService: CustomObjectService,
        private customEntityService: CustomEntityService,
        authService: AuthService,
        toastService: ToastService,
        dialog: MatDialog,
        private router: Router,
        commonService: CommonService,
        private batteryService: BatteryService,
        private translateService: TranslateService,
        gridViewService: GridViewService,
        gridSettingsService: GridSettingsService,
        protected injector: Injector,
        private segService: SegService,
        private organizationLevelService: OrganizationLevelService,
        private mappingGroupService: MappingGroupService,
        private buildingService: CustomMapBuildingService,
        @Optional()
        @Inject(MAT_DIALOG_DATA)
        public searchFieldData: ISearchFieldData,
    ) {
        super(
            toastService,
            dialog,
            commonService,
            authService,
            gridViewService,
            gridSettingsService,
        );
        this.gridService = customEntityService;
        this.currentUser = authService.getCurrentUser();
        this.coId = this.currentUser.preferences?.endPointId;
        this.deleteDisabled = true;
        this.displayedColumns = [];
        this.initializeStaticColumns();
    }

    ngOnInit(): void {
        this.searchDialogRef = this.injector.get(
            MatDialogRef<EndpointsTableComponent>,
            null,
        );
        this.isDialog = !!this.searchDialogRef;

        if (!this.isDialog) {
            this.loadOverviewData();
        }

        if (!this.isDialog && !this.searchFieldData?.coId) {
            this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
                const newCoId = params["coId"];

                if (!newCoId) {
                    this.router.navigate([RoutePaths.unauthorized]);
                    return;
                }

                if (newCoId !== this.currentUser.preferences.endPointId) {
                    this.coId = newCoId;
                    this.authService.setCurrentUserEndPoint(newCoId);
                    this.router
                        .navigateByUrl(`/dashboard/nodes/${newCoId}`)
                        .then(() => {
                            window.location.reload();
                        });
                }
            });
        }

        const filterBy = JSON.stringify({
            type: `${CustomObjectTypeEnum.EmployeeEndPoint},${CustomObjectTypeEnum.EndPoint},${CustomObjectTypeEnum.LocationEndPoint}`,
        });

        this.customObjectService
            .getAll(filterBy)
            .subscribe((result: CustomObjectLookup[]) => {
                this.nodes = result;
                const employeeNode = result.find(
                    (n) => n.type === CustomObjectTypeEnum.EmployeeEndPoint,
                );

                if (!this.isDialog) {
                    if (employeeNode && this.coId === employeeNode.id) {
                        const permissions =
                            this.currentUser.organizationLevelPermissions;

                        if (!permissions?.canAccessIhModule) {
                            this.router.navigate(["dashboard/unauthorized"]);
                            return;
                        }
                    }

                    if (!this.nodes.find((n) => n.id === this.coId)) {
                        const fallbackId =
                            this.nodes.find(n => n.type !== CustomObjectTypeEnum.EmployeeEndPoint)?.id
                            ?? this.nodes[0]?.id;
                        if (fallbackId) {
                            this.router.navigate([RoutePaths.nodes, fallbackId]);
                        } else {
                            this.router.navigate(["dashboard/unauthorized"]);
                        }
                        return;
                    }
                }

                this.setEndPointName();
                this.switchEndPoint(
                    (this.searchFieldData?.coId as string) ?? this.coId,
                );
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected get dynamicPage(): boolean {
        return true;
    }

    protected override get isGridViewEnabled(): boolean {
        return true;
    }

    protected override get objectId(): string {
        if (this.coId == null) {
            return "";
        }
        return `COID_${this.coId}`;
    }

    protected override get deferGridViewInit(): boolean {
        return true;
    }

    get hasOrgFilter() {
        return true;
    }

    override initializeColumns(): void {}

    initializeStaticColumns(): void {
        this.staticDefaultColumnKeys = [
            nameOf<CustomEntity>((x) => x.name),
            "deviceName",
            nameOf<CustomEntity>((x) => x.devices),
            nameOf<CustomEntity>((x) => x.organizationLevelId),
            nameOf<CustomEntity>((x) => x.segId),
            nameOf<CustomEntity>((x) => x.mappingGroupId),
            nameOf<CustomEntity>((x) => x.buildingId),
            nameOf<CustomEntity>((x) => x.latitude),
            nameOf<CustomEntity>((x) => x.longitude),
            nameOf<CustomEntity>((x) => x.useStaticGpsCoordinates),
            nameOf<CustomEntity>((x) => x.hrisOrganizationalUnitId),
            nameOf<CustomEntity>((x) => x.hrisJobId),
            "action",
        ];
        this.columnDetails = {
            [nameOf<CustomEntity>((x) => x.name)]: {
                displayName: "Name",
                type: "string",
                isNullable: false,
                isFilterable: true,
                isSortable: true,
                isDefault: true,
            },
            deviceName: {
                displayName: "Device Name",
                type: "string",
                isNullable: true,
                isFilterable: false,
                isSortable: false,
                isDefault: true,
            },
            [nameOf<CustomEntity>((x) => x.devices)]: {
                displayName: "Devices",
                type: "string",
                isNullable: true,
                isFilterable: false,
                isSortable: false,
                isDefault: true,
                isExportable: false,
            },
            [nameOf<CustomEntity>((x) => x.organizationLevelId)]: {
                displayName: "Organization Level",
                type: "number",
                isNullable: true,
                isFilterable: true,
                isSortable: true,
                isDefault: false,
            },
            [nameOf<CustomEntity>((x) => x.segId)]: {
                displayName: "SEG",
                type: "number",
                isNullable: true,
                isFilterable: true,
                isSortable: true,
                isDefault: false,
            },
            [nameOf<CustomEntity>((x) => x.mappingGroupId)]: {
                displayName: "Mapping Group",
                type: "number",
                isNullable: true,
                isFilterable: true,
                isSortable: true,
                isDefault: false,
            },
            [nameOf<CustomEntity>((x) => x.buildingId)]: {
                displayName: "Building",
                type: "number",
                isNullable: true,
                isFilterable: true,
                isSortable: true,
                isDefault: false,
            },
            [nameOf<CustomEntity>((x) => x.latitude)]: {
                displayName: "Latitude",
                type: "number",
                isNullable: true,
                isFilterable: true,
                isSortable: true,
                isDefault: false,
            },
            [nameOf<CustomEntity>((x) => x.longitude)]: {
                displayName: "Longitude",
                type: "number",
                isNullable: true,
                isFilterable: true,
                isSortable: true,
                isDefault: false,
            },
            [nameOf<CustomEntity>((x) => x.useStaticGpsCoordinates)]: {
                displayName: "Use Static GPS",
                type: "boolean",
                isNullable: true,
                isFilterable: true,
                isSortable: true,
                isDefault: false,
            },
            [nameOf<CustomEntity>((x) => x.hrisOrganizationalUnitId)]: {
                displayName: "HRIS Org Unit",
                type: "number",
                isNullable: true,
                isFilterable: true,
                isSortable: true,
                isDefault: false,
            },
            [nameOf<CustomEntity>((x) => x.hrisJobId)]: {
                displayName: "HRIS Job",
                type: "number",
                isNullable: true,
                isFilterable: true,
                isSortable: true,
                isDefault: false,
            },
            action: {
                displayName: "Action",
                type: "string",
                isNullable: true,
                isFilterable: false,
                isSortable: false,
                isDefault: true,
            },
        };
        this.rebuildColumns();
        this.setDisplayedColumns();
    }

    buildDynamicColumnConfigs(
        primaryNameTitle: string,
    ): ColumnConfig<CustomEntity>[] {
        if (!this.dynamicColumnsLoaded) return [];
        return Object.keys(this.columnDetails)
            .filter((k) => k.startsWith("f_"))
            .filter(
                (k) => this.columnDetails[k].displayName !== primaryNameTitle,
            )
            .map(
                (k) =>
                    new ColumnConfig<CustomEntity>({
                        name: k,
                        displayName: this.columnDetails[k].displayName,
                        cellTemplate: (entity: CustomEntity) => {
                            const value = (
                                entity as CustomEntity & Record<string, unknown>
                            )[k];
                            return value == null ? "" : String(value);
                        },
                    }),
            );
    }

    rebuildColumns(): void {
        const primaryNameTitle =
            this.endPointType === CustomObjectTypeEnum.EmployeeEndPoint
                ? "EmployeeID"
                : `${this.endPointName} Name`;
        if (this.columnDetails[nameOf<CustomEntity>((x) => x.name)]) {
            this.columnDetails[nameOf<CustomEntity>((x) => x.name)] = {
                ...this.columnDetails[nameOf<CustomEntity>((x) => x.name)],
                displayName: primaryNameTitle,
            };
        }
        const nameColumn = new ColumnConfig<CustomEntity>({
            name: nameOf<CustomEntity>((x) => x.name),
            displayName: primaryNameTitle,
            cssClass: (entity) =>
                `device-status ${entity.status ? "activeS" : ""}`,
            cellTemplate: () => "",
            children: () => [
                new ChildColumnConfig<CustomEntity>({
                    onClick: (entity) =>
                        this.isDialog
                            ? this.selectEntityForDialog(entity)
                            : this.openEndpointInfoFlyout(entity.id),
                    cellTemplate: (entity) => entity.name,
                    cssClass: () => "entity-name",
                }),
                new ChildColumnConfig<CustomEntity>({
                    cssClass: () => "expanded-battery",
                    imgSrcs: (entity) =>
                        this.batteryService.getBatteryImgSrcs(entity),
                    imgTooltips: (entity) =>
                        this.batteryService.getBatteryTooltips(entity),
                    cellTemplate: () => "",
                }),
            ],
        });

        const dynamicCols = this.buildDynamicColumnConfigs(primaryNameTitle);

        const deviceNameColumn = new ColumnConfig<CustomEntity>({
            name: "deviceName",
            displayName: "Device Name",
            cellTemplate: () => "",
            children: (row: CustomEntity) =>
                row.devices?.map(
                    (device) =>
                        new ChildColumnConfig<CustomEntity>({
                            cellTemplate: () => device.name,
                            textCssClass: () => "device-name-cell",
                        }),
                ) || [
                    new ChildColumnConfig<CustomEntity>({
                        cellTemplate: () =>
                            this.translateService.getDynamicTranslation(
                                "No Devices",
                            ),
                        textCssClass: () => "disabled",
                    }),
                ],
        });

        const devicesColumn = new ColumnConfig<CustomEntity>({
            name: nameOf<CustomEntity>((x) => x.devices),
            displayName: "Devices",
            isExportable: false,
            cellTemplate: (entity) => entity.devicesCount?.toString() ?? "0",
            children: () => [
                new ChildColumnConfig<CustomEntity>({
                    onClick: (entity) =>
                        this.openNodeAssignmentDialog(entity, true),
                    cssClass: () => "btn-asg",
                    tooltip: () =>
                        this.translateService.getDynamicTranslation("Assign"),
                    cellTemplate: () =>
                        this.translateService.getDynamicTranslation("Assign"),
                    readonly: () =>
                        !this.hasSensorAssignmentPermission(
                            GridPermissions.Create,
                        ),
                }),
                new ChildColumnConfig<CustomEntity>({
                    onClick: (entity) => this.editNodeClick(entity),
                    cssClass: () => "btn-asg",
                    tooltip: () =>
                        this.translateService.getDynamicTranslation("Edit"),
                    cellTemplate: () =>
                        this.translateService.getDynamicTranslation("Edit"),
                    readonly: (entity) =>
                        this.isEditDisabled(entity) ||
                        !this.hasSensorAssignmentPermission(
                            GridPermissions.Update,
                        ),
                }),
                new ChildColumnConfig<CustomEntity>({
                    onClick: (entity) => this.openEndDialog(entity),
                    cssClass: () => "btn-asg",
                    tooltip: () =>
                        this.translateService.getDynamicTranslation("End"),
                    cellTemplate: () =>
                        this.translateService.getDynamicTranslation("End"),
                    readonly: (entity) =>
                        this.isEditDisabled(entity) ||
                        !this.hasSensorAssignmentPermission(
                            GridPermissions.Update,
                        ),
                }),
                new ChildColumnConfig<CustomEntity>({
                    onClick: (entity) =>
                        this.openNodeAssignmentDeleteDialog(entity),
                    cssClass: () => "btn-asg",
                    tooltip: () =>
                        this.translateService.getDynamicTranslation("Delete"),
                    cellTemplate: () =>
                        this.translateService.getDynamicTranslation("Delete"),
                    readonly: (entity) =>
                        this.isDeleteDisabled(entity) ||
                        !this.hasSensorAssignmentPermission(
                            GridPermissions.Delete,
                        ),
                }),
            ],
        });

        const organizationLevelColumn = new ColumnConfig<CustomEntity>({
            name: nameOf<CustomEntity>((x) => x.organizationLevelId),
            displayName: "Organization Level",
            cellTemplate: (entity) => entity.organizationLevelName ?? "-",
        });

        const segColumn = new ColumnConfig<CustomEntity>({
            name: nameOf<CustomEntity>((x) => x.segId),
            displayName: "SEG",
            cellTemplate: (entity) => entity.segName ?? "-",
        });

        const mappingGroupColumn = new ColumnConfig<CustomEntity>({
            name: nameOf<CustomEntity>((x) => x.mappingGroupId),
            displayName: "Mapping Group",
            cellTemplate: (entity) => entity.mappingGroupName ?? "-",
        });

        const buildingColumn = new ColumnConfig<CustomEntity>({
            name: nameOf<CustomEntity>((x) => x.buildingId),
            displayName: "Building",
            cellTemplate: (entity) => entity.buildingName ?? "-",
        });

        const latitudeColumn = new ColumnConfig<CustomEntity>({
            name: nameOf<CustomEntity>((x) => x.latitude),
            displayName: "Latitude",
            cellTemplate: (entity) => entity.latitude?.toString() ?? "-",
        });

        const longitudeColumn = new ColumnConfig<CustomEntity>({
            name: nameOf<CustomEntity>((x) => x.longitude),
            displayName: "Longitude",
            cellTemplate: (entity) => entity.longitude?.toString() ?? "-",
        });

        const useStaticGpsColumn = new ColumnConfig<CustomEntity>({
            name: nameOf<CustomEntity>((x) => x.useStaticGpsCoordinates),
            displayName: "Use Static GPS",
            cellTemplate: (entity) =>
                entity.useStaticGpsCoordinates != null
                    ? entity.useStaticGpsCoordinates
                        ? "Yes"
                        : "No"
                    : "-",
        });

        const hrisOrgUnitColumn = new ColumnConfig<CustomEntity>({
            name: nameOf<CustomEntity>((x) => x.hrisOrganizationalUnitId),
            displayName: "HRIS Org Unit",
            cellTemplate: (entity) =>
                entity.hrisOrganizationalUnitId?.toString() ?? "-",
        });

        const hrisJobColumn = new ColumnConfig<CustomEntity>({
            name: nameOf<CustomEntity>((x) => x.hrisJobId),
            displayName: "HRIS Job",
            cellTemplate: (entity) => entity.hrisJobId?.toString() ?? "-",
        });

        const actionColumn = new ColumnConfig<CustomEntity>({
            name: "action",
            displayName: "Action",
        });
        this.columnConfigs = [
            nameColumn,
            ...dynamicCols,
            deviceNameColumn,
            devicesColumn,
            organizationLevelColumn,
            segColumn,
            mappingGroupColumn,
            buildingColumn,
            latitudeColumn,
            longitudeColumn,
            useStaticGpsColumn,
            hrisOrgUnitColumn,
            hrisJobColumn,
            actionColumn,
        ];
    }

    override objectFilterMap(propertyName: string): any | undefined {
        switch (propertyName) {
            case nameOf<CustomEntity>((x) => x.segId):
                return this.segService;
            case nameOf<CustomEntity>((x) => x.organizationLevelId):
                return this.organizationLevelService;
            case nameOf<CustomEntity>((x) => x.mappingGroupId):
                return this.mappingGroupService;
            case nameOf<CustomEntity>((x) => x.buildingId):
                return this.buildingService;
            default:
                return undefined;
        }
    }

    markDefaultColumns(columns: string[]): void {
        columns.forEach((key) => {
            const details = this.columnDetails[key];
            if (details) {
                this.columnDetails[key] = { ...details, isDefault: true };
            }
        });
    }

    async loadAndBuildColumns(): Promise<void> {
        let details = await firstValueFrom(
            this.customEntityService.getFieldConfiguration(this.coId),
        );
        this.columnDetails = { ...this.columnDetails, ...(details || {}) };
        this.dynamicColumnsLoaded = true;
        this.markDefaultColumns(this.staticDefaultColumnKeys);
        this.rebuildColumns();
        this.setDisplayedColumns();

        if (!this.gridLifecycleInitialized) {
            this.gridLifecycleInitialized = true;

            this.applyRolePermission();

            this.startGridViewInitialization();
            super.ngOnInit();
            super.ngAfterViewInit();
            this.loadHeaderData();
            if (this.pendingGridView) {
                this.applyPendingView();
            }
        } else {
            this.dataSource?.updateData([]);
            if (this.selectedGridView) {
                await super.onGridViewInitialized(this.selectedGridView);
            } else {
                await super.onGridViewInitialized(null);
            }
        }
    }

    override initializeProperties(): void {
        if (!this.isDialog) this.tabs = this.initializeTabs();
        this.addEntityTitle = this.endPointName;
        this.defaultOrderBy = "asc";
        this.searchPlaceholder = `Search ${this.endPointName}`;
    }

    override initializeTabs(): { routerLink: string; title: string }[] {
        const employeeNodeId = this.nodes.find(
            (n) => n.type === CustomObjectTypeEnum.EmployeeEndPoint,
        )?.id;

        if (
            this.endPointType === CustomObjectTypeEnum.EmployeeEndPoint ||
            this.coId === employeeNodeId
        ) {
            return [
                {
                    routerLink: `/dashboard/nodes/${this.coId}`,
                    title: "Workforce",
                },
                ...workforceRouteConfig,
            ];
        } else {
            return [
                ...this.nodes
                    .filter(
                        ({ type }) =>
                            type !== CustomObjectTypeEnum.EmployeeEndPoint,
                    )
                    .map(({ id, name }, index: number) => {
                        if (id === this.coId) {
                            this.selectedTabIndex = index;
                        }
                        return {
                            routerLink: `/dashboard/nodes/${id}`,
                            title: name,
                        };
                    }),
            ];
        }
    }

    override initializeOverviewConfigs(): void {
        this.overviewConfigs = [
            new OverviewConfig({
                title: this.translateService.getDynamicTranslation(
                    "Nodes with Low Battery",
                ),
                value: this.lowBatteryCount,
                onClick: () => this.filterDevicesWithLowBattery(),
            }),
            new OverviewConfig({
                title: this.translateService.getDynamicTranslation(
                    "Online Nodes",
                ),
                value: this.onlineCount,
                onClick: () => this.filterByStatus(true),
            }),
            new OverviewConfig({
                title: this.translateService.getDynamicTranslation(
                    "Offline Nodes",
                ),
                value: this.offlineCount,
                onClick: () => this.filterByStatus(false),
            }),
            new OverviewConfig({
                title: this.translateService.getDynamicTranslation(
                    "Assigned Nodes",
                ),
                value: this.assignedCount,
                onClick: () => this.filterByActiveStatus(true),
            }),
            new OverviewConfig({
                title: this.translateService.getDynamicTranslation(
                    "Unassigned Nodes",
                ),
                value: this.unassignedCount,
                onClick: () => this.filterByActiveStatus(false),
            }),
        ];
    }

    override openEntityDialog(entityId?: string): void {
        this.openCustomEntityDialog(entityId);
    }

    override openEntityDeleteDialog(entity: CustomEntity): void {
        return;
    }

    setEndPointName(): void {
        const node = this.nodes.find((x) => x.id === this.coId);
        this.endPointName = node?.name ?? "Category";
        this.endPointIcon = node?.icon ?? this.DEFAULT_END_POINT_ICON;
        this.endPointType = node?.type;
        this.initializeProperties();
    }

    loadEndPointPage(): void {
        this.loadAndBuildColumns();
    }

    switchEndPoint(endPointId: string): void {
        this.coId = endPointId;
        this.loadEndPointPage();
    }

    protected override async onGridViewInitialized(
        view: GridView | null,
    ): Promise<void> {
        if (!this.dynamicColumnsLoaded) {
            this.pendingGridView = view;
            return;
        }
        await super.onGridViewInitialized(view);
    }

    private applyPendingView(): void {
        if (!this.pendingGridView && this.pendingGridView !== null) return;
        const view = this.pendingGridView;
        this.pendingGridView = null;
        super.onGridViewInitialized(view);
    }

    override getOverviewData(): Observable<void> {
        return this.customEntityService.getNodeOverviewData(this.coId).pipe(
            tap((data) => {
                this.lowBatteryCount = data.lowBatteryCount;
                this.offlineCount = data.offlineCount;
                this.onlineCount = data.onlineCount;
                this.assignedCount = data.assignedCount;
                this.unassignedCount = data.unassignedCount;
            }),
            map(() => undefined),
        );
    }

    filterDevicesWithLowBattery(): void {
        this.additionalFilters["OperatorTypeEnum"] = JSON.stringify({
            Condition: OperatorTypeEnum.LessThan.toString(),
            Field: "sensorassignments.sensor.batterypercentage",
            Value: "30",
        });
    }

    filterByStatus(isOnline: boolean): void {
        this.additionalFilters["OnlineStatus"] = isOnline ? "true" : "false";
    }

    filterByActiveStatus(isAssigned: boolean): void {
        this.additionalFilters["ActiveStatus"] = isAssigned ? "true" : "false";
    }

    openCustomEntityDialog(entityId?: string): void {
        const entityModel: CustomEntityDialogData = {
            id: entityId,
            label: this.endPointName,
            customObjectId: this.coId,
            type: this.endPointType,
        };
        this.openDialog(NodeOperationsComponent, entityModel);
    }

    openNodeAssignmentDialog(endPoint: CustomEntity, assignNew: boolean): void {
        const sensorAssignmentId =
            assignNew && endPoint.devices?.length > 0
                ? ''
                : (endPoint.devices?.[0].sensorAssignmentId ?? '');

        const nodeAssignmentModel: INodeAssignmentFormDialogData = {
            id: sensorAssignmentId,
            nodeId: endPoint.id,
            nodeName: endPoint.name,
            organizationLevelId: endPoint.organizationLevelId,
            isNodeAssignment: true,
        };

        this.openDialog(
            NodeAssignmentDialogComponent,
            nodeAssignmentModel,
            false,
            "550px",
        );
    }

    openNodeAssignmentDeleteDialog(endpoint: CustomEntity): void {
        if (
            !endpoint.devices ||
            !endpoint.devices[0]?.sensorAssignmentId
        ) {
            return;
        }

        this.openDeleteDialog(
            DeleteDialog,
            endpoint,
            this.customEntityService,
            this.endPointName,
            "300px",
        );
    }

    openMultipleNodeAssignmentDialog(endPoint: CustomEntity): void {
        const multipleNodeAssignmentModel: IMultipleNodeAssignmentFormDialogData =
            {
                id: endPoint.id,
                sensorAssignmentIds: endPoint.devices.map(
                    (device) => device.sensorAssignmentId,
                ),
            };

        this.openDialog(
            MultipleSensorAssignmentDialog,
            multipleNodeAssignmentModel,
            false,
            "550px",
        );
    }

    editNodeClick(endPoint: CustomEntity): void {
        if (endPoint.devicesCount > 0) {
            endPoint.devicesCount === 1
                ? this.openNodeAssignmentDialog(endPoint, false)
                : this.openMultipleNodeAssignmentDialog(endPoint);
        }
    }

    private hasSensorAssignmentPermission(
        permission: GridPermissions,
    ): boolean {
        return this.permissionService.hasPermissionFlag(
            RolePermissionObjects.SensorAssignment,
            gridToPermissionFlags[permission],
        );
    }

    isEditDisabled(entity: CustomEntity): boolean {
        return entity.devicesCount === undefined || entity?.devicesCount < 1;
    }

    isDeleteDisabled(entity: CustomEntity): boolean {
        return entity?.devicesCount !== 1;
    }

    selectEntityForDialog(entity: CustomEntity): void {
        if (!this.searchDialogRef) return;

        this.searchDialogRef.close({
            value: entity.id,
            items: entity,
        });
    }

    openEndpointInfoFlyout(nodeId: string): void {
        this.openFlyoutInternal(NodeDetailsPopupComponent, nodeId, "630px");
    }

    openEndDialog(entity: CustomEntity): void {
        if (!entity?.devicesCount || entity.devicesCount < 1) return;

        const dialogModel: IEndDeviceAssignmentDialogData = {
            assignments: entity.devices.map((device) => ({
                deviceId: device.sensorId,
                deviceName: device.name,
                sensorAssignmentId: device.sensorAssignmentId,
            })),
            id: entity.id,
        };

        this.openDialog(EndDeviceAssignmentDialogComponent, dialogModel);
    }

    protected override getExportHeaders(): HttpHeaders | undefined {
        return this.coId
            ? new HttpHeaders().set("coId", this.coId.toString())
            : undefined;
    }
}
